import { useState, useEffect } from "react";
import { API } from "@/App";
import { toast } from "sonner";
import { 
  Wallet,
  ShieldWarning,
  ChartLineUp,
  Bell,
  Lightning,
  Check,
  CurrencyCircleDollar,
  Robot
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";

const CURRENCIES = [
  { code: "USD", symbol: "$", label: "US Dollar" },
  { code: "EUR", symbol: "\u20ac", label: "Euro" },
  { code: "GBP", symbol: "\u00a3", label: "Livre Sterling" },
  { code: "CHF", symbol: "CHF", label: "Franc Suisse" },
  { code: "JPY", symbol: "\u00a5", label: "Yen Japonais" },
  { code: "CAD", symbol: "C$", label: "Dollar Canadien" },
];

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoTradeScanning, setAutoTradeScanning] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API}/settings`);
        const data = await response.json();
        setSettings(data);
      } catch (error) {
        console.error("Error fetching settings:", error);
        toast.error("Erreur lors du chargement des parametres");
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      if (!response.ok) throw new Error("Failed to save settings");
      toast.success("Parametres sauvegardes!");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleAutoTradeScan = async () => {
    setAutoTradeScanning(true);
    try {
      const response = await fetch(`${API}/auto-trade/scan`, { method: 'POST' });
      const data = await response.json();
      if (data.executed > 0) {
        toast.success(`${data.executed} auto-trade(s) execute(s)!`);
      } else {
        toast.info(data.message || "Aucune opportunite qualifiee pour l'auto-trading");
      }
    } catch (error) {
      toast.error("Erreur lors du scan auto-trade");
    } finally {
      setAutoTradeScanning(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="settings-loading">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Parametres</h1>
          <p className="text-sm text-slate-500 font-body mt-1">Configurez votre profil de trading</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="rounded-sm" data-testid="save-settings-btn">
          <Check size={16} className="mr-2" />
          {saving ? "Sauvegarde..." : "Sauvegarder"}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Capital & Risk */}
        <Card className="border-slate-200 rounded-sm shadow-none">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Wallet size={20} className="text-blue-600" />
              <CardTitle className="font-heading text-base">Capital & Risque</CardTitle>
            </div>
            <CardDescription className="font-body">Definissez votre capital et votre tolerance au risque</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="capital" className="font-body">Capital Total (USD)</Label>
              <Input id="capital" type="number" value={settings?.capital || ""} onChange={(e) => updateSetting('capital', parseFloat(e.target.value) || 0)} className="font-data rounded-sm" data-testid="capital-input" />
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <Label className="font-body">Risque par Trade</Label>
                <span className="font-data text-sm font-medium text-slate-900">{settings?.risk_per_trade || 2}%</span>
              </div>
              <Slider value={[settings?.risk_per_trade || 2]} onValueChange={(value) => updateSetting('risk_per_trade', value[0])} max={10} min={0.5} step={0.5} className="w-full" data-testid="risk-slider" />
              <div className="flex justify-between text-xs text-slate-400"><span>0.5%</span><span>10%</span></div>
            </div>
          </CardContent>
        </Card>

        {/* Trading Style */}
        <Card className="border-slate-200 rounded-sm shadow-none">
          <CardHeader>
            <div className="flex items-center gap-2">
              <ChartLineUp size={20} className="text-emerald-600" />
              <CardTitle className="font-heading text-base">Style de Trading</CardTitle>
            </div>
            <CardDescription className="font-body">Choisissez votre approche de trading</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label className="font-body">Style prefere</Label>
              <Select value={settings?.trading_style || "swing"} onValueChange={(value) => updateSetting('trading_style', value)}>
                <SelectTrigger className="rounded-sm" data-testid="style-select"><SelectValue placeholder="Selectionner un style" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="scalping"><div className="flex items-center gap-2"><Lightning size={14} className="text-amber-500" /><span>Scalping (minutes)</span></div></SelectItem>
                  <SelectItem value="day_trading"><div className="flex items-center gap-2"><ChartLineUp size={14} className="text-blue-500" /><span>Day Trading (heures)</span></div></SelectItem>
                  <SelectItem value="swing"><div className="flex items-center gap-2"><ChartLineUp size={14} className="text-emerald-500" /><span>Swing Trading (jours)</span></div></SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="p-3 bg-slate-50 rounded-sm">
              <p className="text-sm text-slate-600 font-body">
                {settings?.trading_style === 'scalping' && "Trades rapides, petits gains repetes. Necessite une attention constante."}
                {settings?.trading_style === 'day_trading' && "Positions ouvertes et fermees dans la journee. Equilibre risque/temps."}
                {settings?.trading_style === 'swing' && "Positions sur plusieurs jours. Capture les mouvements plus importants."}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Alerts */}
        <Card className="border-slate-200 rounded-sm shadow-none">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Bell size={20} className="text-amber-600" />
              <CardTitle className="font-heading text-base">Alertes & Notifications</CardTitle>
            </div>
            <CardDescription className="font-body">Alertes de score + SL/TP automatiques</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <Label className="font-body">Score minimum pour alerte</Label>
                <span className="font-data text-sm font-medium text-slate-900">{settings?.min_score_alert || 85}</span>
              </div>
              <Slider value={[settings?.min_score_alert || 85]} onValueChange={(value) => updateSetting('min_score_alert', value[0])} max={100} min={60} step={5} className="w-full" data-testid="alert-score-slider" />
              <div className="flex justify-between text-xs text-slate-400"><span>60</span><span>100</span></div>
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm">
              <p className="text-sm text-amber-800 font-body">
                Alertes automatiques: score &ge; {settings?.min_score_alert || 85} + notifications Stop-Loss / Take-Profit pour les positions ouvertes
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Auto-Trading */}
        <Card className="border-slate-200 rounded-sm shadow-none">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Robot size={20} className="text-purple-600" />
              <CardTitle className="font-heading text-base">Auto-Trading</CardTitle>
            </div>
            <CardDescription className="font-body">Execution automatique pour les meilleures opportunites</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="font-body">Activer l'auto-trading</Label>
                <p className="text-xs text-slate-500">Execution automatique si score tres eleve</p>
              </div>
              <Switch checked={settings?.auto_trade_enabled || false} onCheckedChange={(checked) => updateSetting('auto_trade_enabled', checked)} data-testid="auto-trade-switch" />
            </div>
            
            {settings?.auto_trade_enabled && (
              <div className="space-y-4 pt-4 border-t border-slate-100">
                <div className="flex justify-between items-center">
                  <Label className="font-body">Score minimum auto-trade</Label>
                  <span className="font-data text-sm font-medium text-slate-900">{settings?.auto_trade_min_score || 90}</span>
                </div>
                <Slider value={[settings?.auto_trade_min_score || 90]} onValueChange={(value) => updateSetting('auto_trade_min_score', value[0])} max={100} min={85} step={1} className="w-full" data-testid="auto-trade-score-slider" />
                
                <Button onClick={handleAutoTradeScan} disabled={autoTradeScanning} variant="outline" className="w-full rounded-sm" data-testid="auto-trade-scan-btn">
                  <Robot size={16} className={`mr-2 ${autoTradeScanning ? 'animate-spin' : ''}`} />
                  {autoTradeScanning ? "Scan en cours..." : "Lancer le Scan Auto-Trade"}
                </Button>
                
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-sm">
                  <p className="text-sm text-purple-800 font-body">
                    L'auto-trading executera des trades automatiquement pour les opportunites avec un score &ge; {settings?.auto_trade_min_score || 90}. Max 3 trades par scan. Taille de position: {settings?.risk_per_trade || 2}% du capital.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Currency & Markets */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        {/* Currency */}
        <Card className="border-slate-200 rounded-sm shadow-none">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CurrencyCircleDollar size={20} className="text-emerald-600" />
              <CardTitle className="font-heading text-base">Devise d'Affichage</CardTitle>
            </div>
            <CardDescription className="font-body">Choisissez votre devise preferee</CardDescription>
          </CardHeader>
          <CardContent>
            <Select value={settings?.preferred_currency || "USD"} onValueChange={(value) => updateSetting('preferred_currency', value)}>
              <SelectTrigger className="rounded-sm" data-testid="currency-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CURRENCIES.map(c => (
                  <SelectItem key={c.code} value={c.code}>
                    <div className="flex items-center gap-2">
                      <span className="font-data">{c.symbol}</span>
                      <span>{c.label} ({c.code})</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400 mt-2 font-body">Les prix seront convertis dans cette devise sur le dashboard</p>
          </CardContent>
        </Card>

        {/* Markets Selection */}
        <Card className="border-slate-200 rounded-sm shadow-none">
          <CardHeader>
            <CardTitle className="font-heading text-base">Marches Preferes</CardTitle>
            <CardDescription className="font-body">Selectionnez les marches que vous souhaitez surveiller</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {['crypto', 'forex', 'stocks'].map((market) => {
                const isSelected = settings?.preferred_markets?.includes(market);
                const labels = { crypto: 'Crypto', forex: 'Forex', stocks: 'Actions' };
                return (
                  <Button key={market} variant={isSelected ? "default" : "outline"} className="rounded-sm"
                    onClick={() => {
                      const current = settings?.preferred_markets || [];
                      const updated = isSelected ? current.filter(m => m !== market) : [...current, market];
                      updateSetting('preferred_markets', updated);
                    }}
                    data-testid={`market-${market}-btn`}
                  >
                    {isSelected && <Check size={14} className="mr-2" />}
                    {labels[market]}
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
